import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery } from "./useApi.js";
import {
  docParsing,
  type BrowseFolderItem,
  type BrowseFileItem,
} from "../api/docParsing.service.js";

const DEFAULT_ROOT = "Shared Documents";

export function useSharePointBrowser(initialPath?: string) {
  const [currentPath, setCurrentPath] = useState(initialPath || DEFAULT_ROOT);

  const {
    data: browseData,
    loading,
    error,
    refetch,
  } = useQuery(() => docParsing.browse(currentPath), [currentPath]);

  const folders = useMemo<BrowseFolderItem[]>(
    () => browseData?.items?.filter((i): i is BrowseFolderItem => i.type === "folder") ?? [],
    [browseData],
  );

  const files = useMemo<BrowseFileItem[]>(
    () => browseData?.items?.filter((i): i is BrowseFileItem => i.type === "file") ?? [],
    [browseData],
  );

  const navigateTo = useCallback((path: string) => {
    setCurrentPath(path);
  }, []);

  const goUp = useCallback(() => {
    const segments = currentPath.split("/");
    if (segments.length > 1) {
      segments.pop();
      setCurrentPath(segments.join("/"));
    }
  }, [currentPath]);

  const canGoUp = currentPath !== DEFAULT_ROOT && currentPath.includes("/");

  const hasActiveJobs = useMemo(
    () => files.some(f => f.parseStatus === "queued" || f.parseStatus === "processing"),
    [files],
  );

  useEffect(() => {
    if (!hasActiveJobs) return;

    const interval = setInterval(() => {
      refetch();
    }, 5_000);

    return () => clearInterval(interval);
  }, [hasActiveJobs, refetch]);

  return {
    currentPath,
    folders,
    files,
    items: browseData?.items ?? [],
    loading,
    error,
    navigateTo,
    goUp,
    canGoUp,
    refetch,
  };
}
